import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlaceIcon from '@mui/icons-material/Place';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useTrip } from '../hooks/useTrip';
import { DeleteTripDialog } from '../components/DeleteTripDialog';
import { TRIP_CATEGORIES, PRIVACY_LEVELS } from '../types';

export function TripDetailPage() {
  const { tripId } = useParams<{ tripId: string }>();
  const navigate = useNavigate();
  const { trip, isLoading, error, deleteTrip } = useTrip(tripId);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteTrip();
      navigate('/trips');
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !trip) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Trip não encontrada'}</Alert>
      </Box>
    );
  }

  const categoryLabel = TRIP_CATEGORIES.find((c) => c.value === trip.category)?.label || trip.category;
  const privacyLabel = PRIVACY_LEVELS.find((p) => p.value === trip.privacy)?.label || trip.privacy;

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/trips')} sx={{ mr: 2 }}>
          Voltar
        </Button>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {trip.title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            <Chip label={categoryLabel} size="small" />
            <Chip label={privacyLabel} size="small" variant="outlined" />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/trips/${tripId}/edit`)}
          >
            Editar
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setShowDeleteDialog(true)}
          >
            Excluir
          </Button>
        </Box>
      </Box>

      {/* Trip Info */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CalendarTodayIcon fontSize="small" color="action" />
              <Typography variant="body1">
                {new Date(trip.startDate).toLocaleDateString('pt-BR')} - {new Date(trip.endDate).toLocaleDateString('pt-BR')}
              </Typography>
            </Box>
            {trip.description && (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {trip.description}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PlaceIcon fontSize="small" color="primary" />
              <Typography variant="body2">
                {trip.pins.length} {trip.pins.length === 1 ? 'pin' : 'pins'}
              </Typography>
            </Box>
          </Paper>

          {/* Photos Section */}
          {trip.photos.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Fotos
              </Typography>
              <ImageList cols={3} gap={8}>
                {trip.photos.map((photo) => (
                  <ImageListItem key={photo.id}>
                    <img
                      src={photo.thumbnailUrl}
                      alt={photo.title || 'Trip photo'}
                      loading="lazy"
                      style={{ borderRadius: 4 }}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            </Paper>
          )}
        </Grid>

        {/* Mini-Map Section */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: 300, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>
              Mapa
            </Typography>
            {trip.pins.length > 0 ? (
              <Box
                sx={{ flex: 1, bgcolor: 'grey.100', borderRadius: 1, position: 'relative', overflow: 'hidden' }}
                id="trip-mini-map"
                aria-label="Mini mapa com pins da trip"
              >
                {/* Map container - Mapbox GL will be initialized here by the map feature */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {trip.pins.length} pins no mapa
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Nenhum pin adicionado
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Pins List */}
          {trip.pins.length > 0 && (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Pins
              </Typography>
              {trip.pins.map((pin) => (
                <Box key={pin.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <PlaceIcon fontSize="small" color="primary" />
                  <Box>
                    <Typography variant="body2">{pin.title || 'Pin sem título'}</Typography>
                    {pin.visitDate && (
                      <Typography variant="caption" color="text.secondary">
                        {new Date(pin.visitDate).toLocaleDateString('pt-BR')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Paper>
          )}
        </Grid>
      </Grid>

      <DeleteTripDialog
        trip={trip}
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />
    </Box>
  );
}
